'use strict';

import { Analytics } from './analytics.js';

// ================================ App State =================================

// Top-level namespace for all AccessibleRemote functions, etc.
let ar = {};

ar.CONFIRMATION_TOOK_TOO_LONG_TIMEOUT_MS  = 3/*min*/ * 60/*sec/min*/ * 1000/*ms/sec*/;
ar.DELAY_BEFORE_CONFIRMATION_POLLING_MS   = 15/*sec*/ * 1000/*ms/sec*/;
ar.CONFIRMATION_POLL_FREQUENCY_MS         = 5/*sec*/ * 1000/*ms/sec*/;

// Global application state
ar.state = {};
ar.state.version                = '';   // Version number
ar.state.msg                    = '';   // For "in-line" messages on main pages
ar.state.errorMessage           = '';   // For unrecoverable errors only
ar.state.clientDeviceId         = null; // UUID that uniquely identifies this client/installation
ar.state.partner                = '';   // comcast, cox, cox-dev, etc.
ar.state.isFlex                 = false;
ar.state.isLoggedIn             = false;
ar.state.easyPairToken          = null; // EasyPair token as returned by startPairing (not confirmed yet)
ar.state.pairingCode            = null; // Pairing code that user should enter on TV using EasyPair app
ar.state.pairingCodeExp         = null; // String version of Unix epoch in ms when pairing code expires
ar.state.confirmInterval        = null; // The interval used to poll for confirmation
ar.state.validationInterval     = null; // The interval used to poll for token validity
ar.state.devices                = [];   // xbo devices if a email/password login is used (vs. EasyPair)
ar.state.selectedXboDeviceId    = null; // device id, either by default or chosen by button or changed via dropdown
ar.state.selectedXboDeviceName  = null; // device name, either by default or chosen by button or changed via dropdown
ar.state.arToken                = null; // custom accessible remote token containing easyPairToken, xboDeviceId, idToken
ar.state.refreshIntervalTimer   = null; // keeps track of refresh token setInterval timer
ar.state.refreshIntervalMs      = null; // interval at which refresh token polling runs
ar.state.keySound               = false;
ar.state.textCommand            = '';
ar.state.isVoiceSupported       = false;
ar.state.isRecording            = false;
ar.state.recordingWarnTimer     = null;
ar.state.recordingStopTimer     = null;
ar.state.isPowerOn              = true; // Assumed!
ar.state.menuOpen				= false;
ar.state.isCompanionMode        = false;
ar.state.isFetchingSeaButtons   = false;
ar.state.errFetchingSeaButtons  = false;
ar.state.haveSeaButtonsData     = false;
ar.state.seaButtonsData         = [];
ar.state.popularSeaButtons      = [];
ar.state.popularSeaButtonsFlat  = [];
ar.state.customMode		        = false;
ar.state.haveTvConfigData       = false;
// this a feature flag for the IFTTT entry in the hamburger menu.
ar.state.tvConfigEnabled        = true;
ar.state.tvConfigData           = {};
// Available languages for internationalization
ar.state.languages = [
	{ flag: 'us', language: 'en', title: 'English' },
	{ flag: 'es', language: 'es', title: 'Español' },
	{ flag: 'fr', language: 'fr', title: 'Français' }
];
// Available partner, strings/constants to be used within the application/UI
ar.state.partners = {
	COMCAST: 'comcast',
	COX: 'cox',
	ROGERS: 'rogers',
	SHAW: 'shaw',
	VIDEOTRON: 'videotron',
	DEFAULT: 'comcast' // To be used for localhost and/or fallback
}

ar.state.aslCommand = "";

ar.setState = function setState(props) {
	if ( props ) {
		for ( var key in props ) {
			if ( ! props.hasOwnProperty(key) ) { continue; }
			ar.state[key] = props[key];
		}
	}
};

// ================================ Utilities =================================

// params:
// - url         : Mandatory
// - method      : Mandatory: One of GET, POST, ...
// - timeout     : Optional. In ms. Default is 0 -- NO TIMEOUT!
// - headers     : Optional. Default {}.
// - data        : Mandatory: String (!)
// - fSuccess    : Mandatory: Signature: f(Anything data, String textStatus, jqXHR jqXHR)
// - fError      : Mandatory: Signature: f(jqXHR jqXHR, String textStatus, String errorThrown)
// - contentType : Default: 'application/x-www-form-urlencoded; charset=UTF-8'
// - dataType    : Default: smart guess (data type of expected result); json, ...
// - processData : Optional. Default is true
ar.request = function request(params) {
	let ajaxParams = {};
	ajaxParams.url = params.url;
	ajaxParams.type = params.method;
	ajaxParams.method = params.method;
	if ( params.timeout ) { ajaxParams.timeout = params.timeout; }
	if ( params.headers ) { ajaxParams.headers = params.headers; }
	ajaxParams.data = params.data;
	ajaxParams.success = function(data, textStatus, jqXHR) {
		if ( jqXHR.status === 401 ) {
			const msg = ar.i18nJ.t('sessionIsNoLongerValid');
			arRouter.push('/login-again?msg='+msg);
			return;
		}
		params.fSuccess.call(null, data, textStatus, jqXHR);
	};
	ajaxParams.error = params.fError;
	if ( params.contentType ) { ajaxParams.contentType = params.contentType; }
	if ( params.dataType ) { ajaxParams.dataType = params.dataType; }
	if ( params.hasOwnProperty('processData') ) { ajaxParams.processData = params.processData; }
	//console.log('=================');
	//console.log(JSON.stringify(ajaxParams, null, 4));
	//console.log('=================');
	$.ajax(ajaxParams);
};

// Log object to server log
ar.log = function log(params) {
	ar.request({
		url: '/api/v1/log',
		method: 'POST',
		timeout: 2000,
		headers: {},
		data: JSON.stringify(params),
		fSuccess: function(data, textStatus, jqXHR) {},
		fError: function(jqXHR, textStatus, errorThrown) {}, // Quiet error!
		contentType: 'application/json; charset=UTF-8'
	});
};

// Example: var foo = ar.getParameterByName('foo') for ...?foo=hello
ar.getParameterByName = function getParameterByName(name, url) {
    if (!url) url = window.location.href;
    name = name.replace(/[\[\]]/g, "\\$&");
    var regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)"),
        results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, " "));
};

ar.chunk = function chunk(array, size) {
	const chunked_arr = [];
	for ( let ii = 0; ii < array.length; ii++ ) {
		const last = chunked_arr[chunked_arr.length - 1];
		if (!last || last.length === size) {
			chunked_arr.push([array[ii]]);
		} else {
			last.push(array[ii]);
		}
	}
	return chunked_arr;
};

ar.showMessage = function showMessage(msg) {
	if ( msg ) {
		ar.setState({ msg: msg });
		const msgContainer = document.getElementById('msg-container');
		if ( msgContainer ) {
			msgContainer.style.display = 'block';
		}
	}
};
ar.clearMessage = function clearMessage() {
	ar.setState({ msg: null });
	const msgContainer = document.getElementById('msg-container');
	if ( msgContainer ) {
		msgContainer.style.display = 'none';
	}
};

ar.translateMessage = function translateMessage(msg) {
	if ( ! msg ) {
		return null;
	}
	if ( msg === 'EXPIRED-EASYPAIR-TOKEN' ) {
		return ar.i18nJ.t('expiredEasyPairToken');
	}
	// Should not happen... means untranslated msg code.
	return ar.i18nJ.t('internalError') + ' (' + msg + ')';
}

// type should be 'localStorage'
ar.isLocalStorageAvailable = function isLocalStorageAvailable(type) {
	try {
		var storage = window[type],
			x = '__storage_test__';
		storage.setItem(x, x);
		storage.removeItem(x);
		return true;
	}
	catch(e) {
		return e instanceof DOMException && (
			// everything except Firefox
			e.code === 22 ||
			// Firefox
			e.code === 1014 ||
			// test name field too, because code might not be present
			// everything except Firefox
			e.name === 'QuotaExceededError' ||
			// Firefox
			e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
			// acknowledge QuotaExceededError only if there's something already stored
			storage.length !== 0;
	}
};

ar.getLocal = function getLocal(key) {
	return localStorage.getItem(key);
}

ar.getLocalBoolean = function getLocalBoolean(key, dflt) {
	const val = localStorage.getItem(key);
	if (val) {
		return JSON.parse(val);
	}
	return dflt;
}

ar.saveLocal = function saveLocal(key, val) {
	localStorage.setItem(key, val);
};

ar.removeLocal = function removeLocal(key) {
	localStorage.removeItem(key);
};

ar.guid = function guid() {
  function s4() {
	return Math.floor((1 + Math.random()) * 0x10000)
	  .toString(16)
	  .substring(1);
  }
  return s4() + s4() + '-' + s4() + '-' + s4() + '-' +
	s4() + '-' + s4() + s4() + s4();
};

ar.beep = function() { var snd = new Audio("data:audio/mp3;base64,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"); snd.play(); }

ar.sound = function(vcode) {
	const usePlainBeeps = false;
	if ( ! usePlainBeeps ) {
		switch (vcode) {
			case 'UP':
				document.getElementById('sound-up').play();
				break;
			case 'DOWN':
				document.getElementById('sound-down').play();
				break;
			case 'LEFT':
			case 'RIGHT':
				document.getElementById('sound-left-or-right').play();
				break;
			case 'ENTER':
				document.getElementById('sound-confirm').play();
				break;
			case 'START-LISTENING':
				document.getElementById('sound-listening').play();
				break;
			case 'STOP-LISTENING':
			document.getElementById('sound-listening-complete').play();
				break;
			default:
				document.getElementById('sound-select').play();
				break;
		}
	} else {
		ar.beep();
	}
}

// ================================ Voice Remote =================================

// https://stackoverflow.com/questions/46910725/record-wav-audio-with-specific-parameters-using-javascript-or-html5
// https://stackoverflow.com/questions/16413063/html5-record-audio-to-file
// https://webaudiodemos.appspot.com/AudioRecorder/js/recorderjs/recorderWorker.js
// https://github.com/mattdiamond/Recorderjs/blob/master/lib/recorder.js

ar.recorder = (function () {

	// Variables
	var recBuffersL=[],recBuffersR=[],
		recorder=null, recording=!1, recLength=0,
		volume=null,audioInput=null,
		numChannels=2, sampleRate=16000, bitsPerSample=16,
		audioContext=null, context=null, outputElement={}, outputString;

	// Older browsers might not implement mediaDevices at all, so we set an empty object first
	if (navigator.mediaDevices === undefined) {
	  navigator.mediaDevices = {};
	}

	// Some browsers partially implement mediaDevices. We can't just assign an object
	// with getUserMedia as it would overwrite existing properties.
	// Here, we will just add the getUserMedia property if it's missing.
	if (navigator.mediaDevices.getUserMedia === undefined) {
		navigator.mediaDevices.getUserMedia = function(constraints) {

			// First get ahold of the legacy getUserMedia, if present
			var getUserMedia = navigator.webkitGetUserMedia || navigator.mozGetUserMedia;

			// Some browsers just don't implement it - return a rejected promise with an error
			// to keep a consistent interface
			if ( ! getUserMedia ) {
				return Promise.reject(new Error('getUserMedia is not implemented in this browser'));
			}

			// Otherwise, wrap the call to the old navigator.getUserMedia with a Promise
			return new Promise(function(resolve, reject) {
				getUserMedia.call(navigator, constraints, resolve, reject);
			});
		}
	}

	// Get User Media
	if (navigator.mediaDevices.getUserMedia) {
		navigator.mediaDevices.getUserMedia(
			{
				audio: {
					sampleRate: 16000
				}
			}
		)
		.then(getUserMediaSuccess)
		.catch(getUserMediaError)
	} else {
	    //alert('getUserMedia not supported in this browser.');
	    getUserMediaError();
	}

	function getUserMediaSuccess(stream) {
		// Enable the voice button
		ar.setState({
			isVoiceSupported: true
		});

		// creates the audio context
		audioContext = window.AudioContext || window.webkitAudioContext;
		context = new audioContext();


		// Browser may not provide the sampleRate we requested
		// We need to record reality!
		sampleRate = context.sampleRate;

		// creates a gain node
		volume = context.createGain();

		// creates an audio node from the microphone incoming stream
		audioInput = context.createMediaStreamSource(stream);

		// connect the stream to the gain node
		audioInput.connect(volume);

		/* From the spec: This value controls how frequently the audioprocess event is
		dispatched and how many sample-frames need to be processed each call.
		Lower values for buffer size will result in a lower (better) latency.
		Higher values will be necessary to avoid audio breakup and glitches */
		var bufferSize = 2048;
		recorder = context.createScriptProcessor(bufferSize, 2, 2);

		recorder.onaudioprocess = function(e) {
			if (!recording) return;
			var left = e.inputBuffer.getChannelData (0);
			var right = e.inputBuffer.getChannelData (1);
			// we clone the samples
			recBuffersL.push(new Float32Array(left));
			recBuffersR.push(new Float32Array(right));
			recLength += bufferSize;
			// Recording now...
		}

		// we connect the recorder
		volume.connect(recorder);
		recorder.connect(context.destination);
	}

	function getUserMediaError(ee) {
		console.log('Error getting user media:');
		console.log(ee);
		// Disable the voice button
		ar.setState({
			isVoiceSupported: false
		});
	}

	function isRecording() {
		return recording;
	}

	function startRecording() {
		recording = true;
		if ( ar.state.keySound ) { ar.sound('START-LISTENING'); }
		clear();
	}

	function stopRecording(cb) {
		var blob;

		recording = false;
		if ( ar.state.keySound ) { ar.sound('STOP-LISTENING'); }
		// Build WAV file (blob)
		exportMonoWAV(function(err, blob) {
			cb(null, blob);
		});
	}

	function clear() {
		recBuffersL.length = recBuffersR.length = 0;
		recLength = 0;
	}

	// Not currently used
	function exportWAV(cb) {
		var bufferL = mergeBuffers(recBuffersL, recLength);
		var bufferR = mergeBuffers(recBuffersR, recLength);
		resample(1, bufferL, recLength, sampleRate, 16000, function(err, renderedAudioBuffer) {
			// @TODO: check err
			var newBufferL = renderedAudioBuffer.getChannelData(0);
			resample(1, bufferR, recLength, sampleRate, 16000, function(err, renderedAudioBuffer) {
				// @TODO: check err
				var newBufferR = renderedAudioBuffer.getChannelData(0);
				const interleaved = interleave(newBufferL, newBufferR);
				recLength = newBufferL.length; // Update global
				var dataview = encodeWAV(interleaved, 16000);
				var audioBlob = new Blob([dataview], { type: 'audio/wav' });
				cb(null, audioBlob);
			});
		});
	}

	function exportMonoWAV(cb) {
		var bufferL = mergeBuffers(recBuffersL, recLength);
		resample(1, bufferL, recLength, sampleRate, 16000, function(err, renderedAudioBuffer) {
			// @TODO: check err
			bufferL = renderedAudioBuffer.getChannelData(0);
			recLength = bufferL.length;
			var dataview = encodeWAV(bufferL, 16000, true);
			var audioBlob = new Blob([dataview], { type: 'audio/wav' });
			cb(null, audioBlob);
		});
	}

	// See https://stackoverflow.com/questions/27598270/resample-audio-buffer-from-44100-to-16000
	// See https://gist.github.com/jhiswin/b88ecf7900b76810429b
	function resample(inNumChannels, inBuffer, inNumSamples, inSampleRate, outSampleRate, cb) {
		var offlineContext, audioBuffer, channel, buf, ii;

		// Get an OfflineAudioContext at 16000Hz (the target sample rate)
		offlineContext = new OfflineAudioContext(inNumChannels, inNumSamples, outSampleRate);

		// Get an empty AudioBuffer at whatever sample rate the browser is using (e.g., 48000)
		audioBuffer = offlineContext.createBuffer(inNumChannels, inNumSamples, inSampleRate);

		// Copy your data in the AudioBuffer
		for ( channel = 0; channel < inNumChannels; channel++ ) {
			buf = audioBuffer.getChannelData(channel);
			for ( ii = 0; ii < inNumSamples; ii++ ) {
				buf[ii] = inBuffer[ii];
			}
		}

		// Play it from the beginning
		var source = offlineContext.createBufferSource();
		source.buffer = audioBuffer;
		source.connect(offlineContext.destination);
		source.start(0);
		offlineContext.startRendering().then(function(renderedBuffer) {
			// Rendering completed successfully
		});

		offlineContext.oncomplete = function(ev) {
			// ev is an OfflineAudioCompletionEvent
			// audioBuffer contains audio resampled at 16000Hz
			// Use audioBuffer.getChannelData(x) to get an ArrayBuffer for channel x
			cb(null, ev.renderedBuffer);
		};
	}

	// Return DataView representing our WAV file
	// See http://soundfile.sapp.org/doc/WaveFormat/
	// mono is optional; false if not provided (stereo is default)
	function encodeWAV(samples, sampleRate, mono) {
		var buffer = new ArrayBuffer(44 + samples.length * 2);
		var view = new DataView(buffer);

		// RIFF header

		// - ChunkID
		writeUTFBytes(view, 0, 'RIFF');
		// - ChunkSize (36 + SubChunk2Size = 4 + (8+SubChunk1Size) + (8+SubChunk2Size))
		view.setUint32(4, 36 + samples.length * 2, true);
		// - Format
		writeUTFBytes(view, 8, 'WAVE');

		// fmt sub-chunk

		// - Subchunk1ID (Note the trailing space)
		writeUTFBytes(view, 12, 'fmt ');
		// - Subchunk1Size (16 for PCM; Size of the rest of the Subchunk following this number)
		view.setUint32(16, 16, true);
		// - AudioFormat (1 for PCM, i.e., Linear quantization)
		view.setUint16(20, 1, true);
		// - NumChannels (1=mono, 2=stereo)
		view.setUint16(22, (mono?1:2), true);
		// - SampleRate
		view.setUint32(24, sampleRate, true);
		// - ByteRate (SampleRate * NumChannels * BitsPerSample/8) = SampleRate * BlockAlign
		view.setUint32(28, sampleRate * numChannels * 2, true);
		// - BlockAlign (NumChannels * BitsPerSample/8 = NumChannels * BytesPerSample)
		view.setUint16(32, numChannels * 2, true);
		// - BitsPerSample (8 bits = 8; 16 bits = 16; etc.)
		view.setUint16(34, bitsPerSample, true);

		// data sub-chunk

		// - Subchunk2ID
		writeUTFBytes(view, 36, 'data');
		// - Subchunk2Size (NumSamples * NumChannels * BitsPerSample/8)
		//   samples.length already has the *NumChannels; 2 below = 16/8
		view.setUint32(40, samples.length * 2, true); // data chunk length

		// - Data (write the PCM samples)
		floatTo16BitPCM(view, 44, samples);

		return view;
	}

	function floatTo16BitPCM(output, offset, input){
		for (var i = 0; i < input.length; i++, offset+=2){
			var s = Math.max(-1, Math.min(1, input[i]));
			output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
		}
	}

	// Interleave the float32s within two Float32Arrays into one interleaved Float32Array
	function interleave(inputL, inputR){
		var length = inputL.length + inputR.length;
		var result = new Float32Array(length);

		var index = 0,
			inputIndex = 0;

		while (index < length){
			result[index++] = inputL[inputIndex];
			result[index++] = inputR[inputIndex];
			inputIndex++;
		}
		return result;
	}

	// Flatten an array of Float32Arrays into one single Float32Array and return
	function mergeBuffers(recBuffers, recLength){
		var result = new Float32Array(recLength);
		var offset = 0;
		for (var i = 0; i < recBuffers.length; i++){
			result.set(recBuffers[i], offset);
			offset += recBuffers[i].length;
		}
		return result;
	}

	function writeUTFBytes(view, offset, string) {
		var lng = string.length;
		for (var i = 0; i < lng; i++){
			view.setUint8(offset + i, string.charCodeAt(i));
		}
	}

	// Not currently used
	function convertFloat32ToInt16(buffer) {
		var ll = buffer.length;
		var buf = new Int16Array(1);
		while ( ll-- ) {
			buf[ll] = buffer[ll] * 0xFFFF; // convert to 16 bit
		}
		return buf.buffer;
	}

	return {
		isRecording: isRecording,
		start : startRecording,
		stop  : stopRecording
	};

}());

ar.Uint8ToBase64 = function Uint8ToBase64(u8Arr){
  var CHUNK_SIZE = 0x8000; //arbitrary number
  var index = 0;
  var length = u8Arr.length;
  var result = '';
  var slice;
  while (index < length) {
    slice = u8Arr.subarray(index, Math.min(index + CHUNK_SIZE, length));
    result += String.fromCharCode.apply(null, slice);
    index += CHUNK_SIZE;
  }
  return btoa(result);
};

ar.postStopRecording = function(err, blob) {
	// Recording stopped

	// 1. Convert Blob object into an ArrayBuffer object
	var arrayBuffer;
	var fileReader = new FileReader();
	fileReader.onload = function() {
		arrayBuffer = this.result;             // ArrayBuffer
		var u8 = new Uint8Array(arrayBuffer);  // ArrayBuffer -> Uint8Array
		var voiceData = ar.Uint8ToBase64(u8);  // Uint8Array -> Base64 encoded String

		// Submit WAV file to system
		ar.request({
			url: '/api/v1/voice',
			method: 'POST',
			timeout: 7000,
			headers     : {
				'Authorization': 'Bearer ' + ar.state.arToken
			},
			processData: false,
			data: JSON.stringify({
				voiceData      : voiceData
			}),
			contentType : 'application/json; charset=UTF-8',
			fSuccess: function(data, textStatus, jqXHR) {
				// Quiet success; the TV will show reaction/results
			},
			fError: function(jqXHR, textStatus, errorThrown) {
				// Quiet failure; the TV will show or not show results
			}
		});
	};
	fileReader.readAsArrayBuffer(blob);
};

// ================================ App Logic =================================

// For parnter "foo", return "foo"; For partner "Fooo-Dev", return "foo"
ar.getCorePartnerFromPartner = function getCorePartnerFromPartner(partner) {
	let lcPartner = ( partner ? partner.toLowerCase() : '' );	// Foo -> foo, Foo-Dev -> foo-dev, etc.
	const pieces = lcPartner.split('-');
	lcPartner = pieces[0];										// foo -> foo, foo-dev -> foo
	return lcPartner;
};

ar.getPartnerFromUrl = function getPartnerFromUrl(url) {
	let partner;

    if (!url) url = window.location.href;

    partner = ar.getParameterByName('partner', url);
    if ( partner ) { return partner; }

    // Always return "simple" (prod-style) partners when running deployed somewhere (even staging)
	if (url.indexOf('localhost') !== -1) { return ar.state.partners.DEFAULT; }
	// WebRemote.com domain-style partners
	if ( url.indexOf('rogers.webremote.com') !== -1 ) { return ar.state.partners.ROGERS; }
	// Partner domain-style partners
    if ( url.indexOf('comcast.net') !== -1 ) { return ar.state.partners.COMCAST; }
    if ( url.indexOf('comcast.com') !== -1 ) { return ar.state.partners.COMCAST; }
    if ( url.indexOf('cox.net') !== -1 ) { return ar.state.partners.COX; }
    if ( url.indexOf('cox.com') !== -1 ) { return ar.state.partners.COX; }
    if ( url.indexOf('shaw.ca') !== -1 ) { return ar.state.partners.SHAW; }
    // Default
    return ar.state.partners.DEFAULT;
}

ar.getPartner = function getPartner(url) {
	let partner = ar.getPartnerFromUrl(url);
	// Partner is never null, always falls back to default partner
	// Therefore, there is no need to save it in local storage
	ar.setState({ partner });
	return partner;
}

ar.getDefaultLocaleFromUrl = function getDefaultLocaleFromUrl(url) {
	const partner = ar.getPartnerFromUrl(url);
	const corePartner = ar.getCorePartnerFromPartner(partner);
	if ( corePartner === ar.state.partners.VIDEOTRON ) { return 'fr'; }
	return 'en';
};

ar.partnerLogoUrl = function partnerLogoUrl(partner) {
	const corePartner = ar.getCorePartnerFromPartner(partner);
	let file;
	if ( corePartner === ar.state.partners.COMCAST ) {
		file = 'logo-xfinity.svg';
	} else if ( corePartner === ar.state.partners.COX ) {
		file = 'cox_103x34.png';
	} else if ( corePartner === ar.state.partners.ROGERS ) {
		file = 'rogers_xfinity_stacked_white.png';
	} else if ( corePartner === ar.state.partners.SHAW ) {
		file = 'shaw_logo_REV_300x100.svg';
	} else {
		file = 'logo-noPartner.svg';
	}
	return '/images/logos/'+file;
};

ar.partnerLogoAltText = function partnerLogoAltText(partner) {
	const corePartner = ar.getCorePartnerFromPartner(partner);
	let altText;
	if ( corePartner === ar.state.partners.COMCAST ) {
		altText = 'Xfinity';
	} else if ( corePartner === ar.state.partners.COX ) {
		altText = 'Cox';
	} else if ( corePartner === ar.state.partners.SHAW ) {
		altText = 'Shaw';
	} else {
		altText = '';
	}
	return altText;
};

ar.shouldShowReleaseNotesLink = function shouldShowReleaseNotesLink(partner) {
	if ( partner === ar.state.partners.COMCAST ) { return true; }
	return false;
};

// Normally called with undefined or null, but if a query string clientDeviceId
// is given, it will be used if there isn't already one in localStorage
ar.ensureClientDeviceId = function ensureClientDeviceId(qsClientDeviceId) {
	let clientDeviceId = ar.getLocal('clientDeviceId');
	if (! clientDeviceId ) {
		// We haven't already generated and saved a clientDeviceId
		// It's either the very first visit or it's after a localStorage clear.
		if ( qsClientDeviceId ) {
			// Use client device given if we have one, and save it
			clientDeviceId = qsClientDeviceId;
			ar.saveLocal('clientDeviceId', clientDeviceId);
		} else {
			// Generate and save a new client device ID
			clientDeviceId = ar.guid();
			ar.saveLocal('clientDeviceId', clientDeviceId);
		}
	}
	ar.setState({
		clientDeviceId : clientDeviceId // Stash in memory
	});

	if ( clientDeviceId ) {
		Analytics.identify({ userId: clientDeviceId });
	}
};

// Called when setTimeout fires. If we've moved out of 'WAITING-FOR-TV' state,
// then this will quietly do nothing.
ar.confirmationTookTooLong = function confirmationTookTooLong() {
	if ( window.location.href.indexOf('/waiting-for-tv') !== -1 ) {
		// Seems like we're stuck in this state for too long. Give up.
		// Cancel the confirmInterval while we're at it
		window.clearInterval(ar.state.confirmInterval);
		ar.setState({
			confirmInterval : null
		});
		const msg = ar.i18nJ.t('confirmationTookTooLong');
		arRouter.push('/error?msg='+msg);
		return;
	}
};

ar.confirmPairingSuccess = function confirmPairingSuccess(data, textStatus, jqXHR) {
	if ( jqXHR.status === 200 ) {
		// We can get called with a 200 but with only exp:xxx (no status:CONFIRMED, etc.)
		if ( data.status !== 'CONFIRMED' ) {
			// Do nothing... leave the interval running
			return;
		}
		window.clearInterval(ar.state.confirmInterval);
		ar.setState({
			confirmInterval : null,
			arToken         : data.arToken
		});
		ar.saveLocal('arToken', data.arToken);
		// arRouter.push('/remote');
		ar.goToCorrectRemotePage();
	} else if ( jqXHR.status !== 202 ) {
		// Something's weird...
		window.clearInterval(ar.state.confirmInterval);
		ar.log({
			clientDeviceId: ar.state.clientDeviceId,
			code: 'CONFIRM-PAIRING-INTERNAL-ERROR-2',
			message: 'An unusual internal error occurred trying to confirm your pairing code',
			textStatus: textStatus,
			data: data,
			jqXHR_status: jqXHR.status,
			jqXHR_responseJSON: jqXHR.responseJSON
		});
		const msg = ar.i18nJ.t('unusualErrorConfirmingCode');
		arRouter.push('/error?msg='+msg);
	}
};

ar.confirmPairingError = function confirmPairingError(jqXHR, textStatus, errorThrown) {
	// Could be a 405, ... or 500
	ar.log({
		clientDeviceId: ar.state.clientDeviceId,
		code: 'CONFIRM-PAIRING-INTERNAL-ERROR-1',
		message: 'An internal error occurred trying to confirm a pairing code',
		textStatus: textStatus,
		errorThrown: errorThrown,
		jqXHR_responseJSON: jqXHR.responseJSON
	});
	window.clearInterval(ar.state.confirmInterval);
	ar.setState({
		confirmInterval : null
	});
	const msg = ar.i18nJ.t('internalErrorGettingCode');
	arRouter.push('/error?msg='+msg);
};

// Called each time the setInterval fires (for pairing confirmation).
ar.confirmPairing = function confirmPairing() {
	// Check pairing confirmation with EasyPair service
	ar.request({
		url         : '/api/v1/pairing/confirm',
		method      : 'POST',
		timeout     : 5000,
		headers     : {
			'Authorization': 'Bearer ' + ar.state.easyPairToken
		},
		data        : JSON.stringify({
			partner        : ar.state.partner,       // For debugging/logging
			clientDeviceId : ar.state.clientDeviceId // For debugging/logging
		}),
		fSuccess    : ar.confirmPairingSuccess,
		fError      : ar.confirmPairingError,
		contentType : null,
		dataType    : 'json'
	});
};

ar.startConfirmPairingPolling = function startConfirmPairingPolling() {
	// Create timer that will enforce a "it's taken too long" timeout
	window.setTimeout(ar.confirmationTookTooLong, ar.CONFIRMATION_TOOK_TOO_LONG_TIMEOUT_MS);
	// Start interval that polls for pairing confirmation
	// Delay a bit before the interval actually starts to prevent a few unnecessary calls
	window.setTimeout(function() {
		var confirmInterval = window.setInterval(ar.confirmPairing, ar.CONFIRMATION_POLL_FREQUENCY_MS);
		ar.setState({
			confirmInterval: confirmInterval
		});
	}, ar.DELAY_BEFORE_CONFIRMATION_POLLING_MS);
};

ar.startPairingSuccess = function startPairingSuccess(data, textStatus, jqXHR) {
	if ( jqXHR.status === 200 && data && data.token && data.pairingCode ) {
		// We got a 200 with the data we want/need
		ar.setState({
			easyPairToken  : data.token,
			pairingCode    : data.pairingCode,
			pairingCodeExp : data.exp
		});
		ar.clearMessage();
		ar.startConfirmPairingPolling();
		arRouter.push('/waiting-for-tv');
	} else {
		// Something's weird
		ar.log({
			clientDeviceId: ar.state.clientDeviceId,
			code: 'START-PAIRING-INTERNAL-ERROR-2',
			message: 'An unusual internal error occurred trying to get a pairing code',
			textStatus: textStatus,
			data: data,
			jqXHR_status: jqXHR.status,
			jqXHR_responseJSON: jqXHR.responseJSON
		});
		const msg = ar.i18nJ.t('unusualErrorGettingCode');
		arRouter.push('/error?msg='+msg);
	}
};

ar.startPairingError = function startPairingError(jqXHR, textStatus, errorThrown) {
	// Could be a 415, 405, 400, 423, 401, ... or 500
	ar.log({
		clientDeviceId: ar.state.clientDeviceId,
		code: 'START-PAIRING-INTERNAL-ERROR-1',
		message: 'An internal error occurred trying to get a pairing code',
		textStatus: textStatus,
		errorThrown: errorThrown,
		jqXHR_responseJSON: jqXHR.responseJSON
	});
	const msg = ar.i18nJ.t('internalErrorGettingCode');
	arRouter.push('/error?msg='+msg);
};

ar.startPairing = function startPairing() {
	// Start pairing with EasyPair service
	ar.request({
		url         : '/api/v1/pairing/start',
		method      : 'POST',
		timeout     : 10000,
		headers     : {},
		data        : JSON.stringify({
			partner        : ar.state.partner,        // For debugging/logging
			clientDeviceId : ar.state.clientDeviceId  // For debugging/logging *** AND USED AS PAIRING CLIENT ID ***
		}),
		fSuccess    : ar.startPairingSuccess,
		fError      : ar.startPairingError,
		contentType : 'application/json; charset=UTF-8',
		dataType    : 'json'
	});
};

ar.getVersionSuccess = function getVersionSuccess(data, textStatus, jqXHR) {
	if ( jqXHR.status === 200 && data ) {
		// We got a 200 with the data we want/need
		ar.setState({
			version: data.version,
			language: data.language || 'en'
		});
		if(data.cb) {
			data.cb(data.language || 'en');
		}
	} else {
		// Something's weird
		ar.log({
			clientDeviceId: ar.state.clientDeviceId,
			code: 'GET-VERSION-ERROR-2',
			message: 'An internal error occurred while trying to get version.',
			textStatus: textStatus,
			data: data,
			jqXHR_status: jqXHR.status,
			jqXHR_responseJSON: jqXHR.responseJSON
		});
		// DON'T GO TO /error
	}
};

ar.getVersionError = function getVersionError(jqXHR, textStatus, errorThrown) {
	ar.log({
		clientDeviceId: ar.state.clientDeviceId,
		code: 'GET-VERSION-ERROR-1',
		message: 'An internal error occurred while trying to get version.',
		textStatus: textStatus,
		errorThrown: errorThrown,
		jqXHR_responseJSON: jqXHR.responseJSON
	});
	// DON'T GO TO /error
};

ar.getVersion = function getVersion(cb) {
	if ( ar.state.version ) { return; }
	ar.request({
		url         : '/api/v1/version',
		method      : 'GET',
		timeout     : 2000,
		headers     : {
			'Authorization': 'Bearer ' + ar.state.arToken
		},
		data        : null,
		fSuccess    : function(data, textStatus, jqXHR){
			data.cb = cb;
			ar.getVersionSuccess(data, textStatus, jqXHR);
		},
		fError      : ar.getVersionError,
		contentType : 'application/json; charset=UTF-8',
		dataType    : 'json'
	});
};

ar.getSeaButtonImage = function getSeaButtonImage(cmd, cb) {
	ar.request({
		url         : '/api/v1/seabuttons/image',
		method      : 'GET',
		timeout     : 10000,
		headers     : {
			'Authorization': 'Bearer ' + ar.state.arToken
		},
		data        : {
			cmd : cmd
		},
		fSuccess    : function(data, textStatus, jqXHR){
			if ( cb ) {
				cb(null, data);
			}
		},
		fError      : function(jqXHR, textStatus, errorThrown) {
			if ( cb ) {
				cb({
					status: 'ERROR'
				});
			}
		},
		contentType : 'application/json; charset=UTF-8',
		dataType    : 'json'
	});
};

ar.getSeaButtonsDataSuccess = function getSeaButtonsDataSuccess(data, textStatus, jqXHR) {
	if ( jqXHR.status === 200) {
		// We got a 200 with the data we want/need
		// A bug can cause null entries in the data.seaButtonsData array... remove them here as a work-around
		const seaButtonsData = data.seaButtonsData?.filter(item => item !== null);
		if ( data.seaButtonsData.length !== seaButtonsData.length ) {
			ar.log({
				clientDeviceId: ar.state.clientDeviceId,
				code: 'REPAIRED-SEABUTTONS',
				message: 'Repaired custom buttons data'
			});
		}
		ar.setState({
			isFetchingSeaButtons: false,
			errFetchingSeaButtons: false,
			haveSeaButtonsData: true,
			seaButtonsData: seaButtonsData
		});
		if(data.cb) {
			data.cb();
		}
	} else {
		// Something's weird
		ar.log({
			clientDeviceId: ar.state.clientDeviceId,
			code: 'GET-SEABUTTONS-ERROR-2',
			message: 'An internal error occurred while trying to get Simple, Easy, Awesome Buttons.',
			textStatus: textStatus,
			data: data,
			jqXHR_status: jqXHR.status,
			jqXHR_responseJSON: jqXHR.responseJSON
		});
		ar.setState({
			isFetchingSeaButtons: false,
			errFetchingSeaButtons: true,
			haveSeaButtonsData: false,
			seaButtonsData: []
		});
		const msg = ar.i18nJ.t('internalErrorOccurred');
		arRouter.push('/error?msg='+msg);
	}
};

ar.getSeaButtonsDataError = function getSeaButtonsDataError(jqXHR, textStatus, errorThrown) {
	ar.log({
		clientDeviceId: ar.state.clientDeviceId,
		code: 'GET-SEABUTTONS-ERROR-1',
		message: 'An internal error occurred while trying to get Simple, Easy, Awesome Buttons.',
		textStatus: textStatus,
		errorThrown: errorThrown,
		jqXHR_responseJSON: jqXHR.responseJSON
	});
	ar.setState({
		isFetchingSeaButtons: false,
		errFetchingSeaButtons: true,
		haveSeaButtonsData: false,
		seaButtonsData: []
	});
	const msg = ar.i18nJ.t('internalErrorGettingBtns');
	arRouter.push('/error?msg='+msg);
};

ar.getSeaButtonsData = function getSeaButtonsData(cb) {
	if ( ! this.state.arToken ) { return; }

	ar.setState({ isFetchingSeaButtons: true });
	ar.request({
		url         : '/api/v1/seabuttons',
		method      : 'GET',
		timeout     : 10000,
		headers     : {
			'Authorization': 'Bearer ' + ar.state.arToken
		},
		data        : null,
		fSuccess    : function(data, textStatus, jqXHR){
			data.cb = cb;
			ar.getSeaButtonsDataSuccess(data, textStatus, jqXHR);
		},
		fError      : ar.getSeaButtonsDataError,
		contentType : 'application/json; charset=UTF-8',
		dataType    : 'json'
	});
};

ar.saveSeaButtonsDataSuccess = function saveSeaButtonsDataSuccess(data, textStatus, jqXHR) {
	if ( jqXHR.status === 200 ) {	
		// Quiet; UI will show new custom buttons
	} else {
		// Something's weird
		ar.log({
			clientDeviceId: ar.state.clientDeviceId,
			code: 'SAVE-SEABUTTONS-ERROR-2',
			message: 'An internal error occurred while trying to save.',
			textStatus: textStatus,
			data: data,
			jqXHR_status: jqXHR.status,
			jqXHR_responseJSON: jqXHR.responseJSON
		});
		const msg = ar.i18nJ.t('internalErrorOccurred');
		arRouter.push('/error?msg='+msg);
	}
};

ar.saveSeaButtonsDataError = function saveSeaButtonsDataError(jqXHR, textStatus, errorThrown) {
	ar.log({
		clientDeviceId: ar.state.clientDeviceId,
		code: 'SAVE-SEABUTTONS-ERROR-1',
		message: 'An internal error occurred while trying to save.',
		textStatus: textStatus,
		errorThrown: errorThrown,
		jqXHR_responseJSON: jqXHR.responseJSON
	});
	const msg = ar.i18nJ.t('internalErrorSavingBtns');
	arRouter.push('/error?msg='+msg);
};

ar.saveSeaButtonsData = function saveSeaButtonsData() {
	ar.request({
		url         : '/api/v1/seabuttons',
		method      : 'POST',
		timeout     : 10000,
		headers     : {
			'Authorization' : `Bearer ${ar.state.arToken}`
		},
		data        : JSON.stringify({
			seaButtonsData : ar.state.seaButtonsData
		}),
		fSuccess    : ar.saveSeaButtonsDataSuccess,
		fError      : ar.saveSeaButtonsDataError,
		contentType : 'application/json; charset=UTF-8',
		dataType    : 'json'
	});
};

ar.getPopularSeaButtonsSuccess = function getPopularSeaButtonsSuccess(data, textStatus, jqXHR) {
	if ( jqXHR.status === 200 && data ) {
		// We got a 200 with the data we want/need
		let popularSeaButtonsFlat = [];
		data.popularButtons.map((category) => {
			category.buttons.map((button) => {
				popularSeaButtonsFlat.push(button);
			})
		})
		ar.setState({
			popularSeaButtons: data.popularButtons || [],
			popularSeaButtonsFlat: popularSeaButtonsFlat
		});
		if(data.cb) {
			data.cb(null, data.popularButtons);
		}
	} else {
		// Something's weird
		ar.log({
			clientDeviceId: ar.state.clientDeviceId,
			code: 'GET-POPULAR-BUTTONS-ERROR-2',
			message: 'An internal error occurred while trying to get popular buttons.',
			textStatus: textStatus,
			data: data,
			jqXHR_status: jqXHR.status,
			jqXHR_responseJSON: jqXHR.responseJSON
		});
		if(data.cb) {
			data.cb({ status: 'ERROR' });
		}
		// DON'T GO TO /error
	}
};

ar.getPopularSeaButtonsError = function getPopularSeaButtonsError(jqXHR, textStatus, errorThrown) {
	ar.log({
		clientDeviceId: ar.state.clientDeviceId,
		code: 'GET-POPULAR-BUTTONS-ERROR-1',
		message: 'An internal error occurred while trying to get popular buttons.',
		textStatus: textStatus,
		errorThrown: errorThrown,
		jqXHR_responseJSON: jqXHR.responseJSON
	});
	// DON'T GO TO /error
};

ar.getPopularSeaButtons = function getPopularSeaButtons(cb) {
	if ( ar.state.popularSeaButtons && ar.state.popularSeaButtons.length > 0 ) { return; }
	ar.request({
		url         : '/api/v1/seabuttons/popular',
		method      : 'GET',
		timeout     : 2000,
		headers     : {
			'Authorization': 'Bearer ' + ar.state.arToken
		},
		data        : null,
		fSuccess    : function(data, textStatus, jqXHR){
			data.cb = cb;
			ar.getPopularSeaButtonsSuccess(data, textStatus, jqXHR);
		},
		fError      : ar.getPopularSeaButtonsError,
		contentType : 'application/json; charset=UTF-8',
		dataType    : 'json'
	});
};

ar.getAccountSuccess = function getAccountSuccess(data, textStatus, jqXHR) {
	if ( jqXHR.status === 200 && data ) {
		// We got a 200 with the data we want/need
		// data also contains zipCode, zipCode4, and timeZone if ever need
		ar.setState({
		  isFlex : data.isFlex
		});
		ar.saveLocal('isFlex', data.isFlex);
		if(data.cb) {
			data.cb();
		}
	} else {
		// Something's weird
		ar.log({
			clientDeviceId: ar.state.clientDeviceId,
			code: 'GET-ACCOUNT-ERROR-2',
			message: 'Could not retrieve account information.',
			textStatus: textStatus,
			data: data,
			jqXHR_status: jqXHR.status,
			jqXHR_responseJSON: jqXHR.responseJSON
		});
		// Reasonable default
		ar.setState({
		  isFlex : false
		});
		ar.saveLocal('isFlex', false);
	}
};

ar.getAccountError = function getAccountError(jqXHR, textStatus, errorThrown) {
	ar.log({
		clientDeviceId: ar.state.clientDeviceId,
		code: 'GET-ACCOUNT-ERROR-1',
		message: 'An internal error occurred while trying to retrieve account information.',
		textStatus: textStatus,
		errorThrown: errorThrown,
		jqXHR_responseJSON: jqXHR.responseJSON
	});
	// Reasonable default
	ar.setState({
	  isFlex : false
	});
	ar.saveLocal('isFlex', false);
};

ar.getAccount = function getAccount(cb) {
	ar.request({
		url         : '/api/v1/account',
		method      : 'POST',
		timeout     : 10000,
		headers     : {
			'Authorization': 'Bearer ' + ar.state.arToken
		},
		data        : null,
		fSuccess    : function(data, textStatus, jqXHR){
			data.cb = cb;
			ar.getAccountSuccess(data, textStatus, jqXHR);
		},
		fError      : ar.getAccountError,
		contentType : 'application/json; charset=UTF-8',
		dataType    : 'json'
	});
};

ar.getDevicesSuccess = function getDevicesSuccess(data, textStatus, jqXHR) {
	if ( jqXHR.status === 200 && data && data.devices && data.devices.length > 0 ) {
		// We got a 200 with the data we want/need
		if (data.devices.length === 1) {
			ar.setState({
				devices             : data.devices,
				selectedXboDeviceId : data.devices[0].id
			});
			ar.saveLocal('devices', JSON.stringify(data.devices));
			ar.saveLocal('deviceId', data.devices[0].id);
			ar.getARToken(); // Will go to /remote if successful, /error if not
		} else {
			ar.setState({
				devices : data.devices
			});
			ar.saveLocal('devices', JSON.stringify(data.devices));
			if ( window.location.pathname !== '/choose-device' ) {
				arRouter.push('/choose-device');
			}
		}
	} else {
		// Something's weird
		ar.log({
			clientDeviceId: ar.state.clientDeviceId,
			code: 'CHOOSE-DEVICE-ERROR-2',
			message: 'No compatible set top box found.',
			textStatus: textStatus,
			data: data,
			jqXHR_status: jqXHR.status,
			jqXHR_responseJSON: jqXHR.responseJSON
		});
		const msg = ar.i18nJ.t('noCompatibleStbFound');
		arRouter.push('/error?msg='+msg);
	}
};

ar.getTvConfigDataSuccess = function getTvConfigDataSuccess(data, textStatus, jqXHR) {
	if ( jqXHR.status === 200 ) {
		// We got a 200 with the data we want/need
		ar.setState({
			haveTvConfigData: true,
			tvConfigData: data.tvConfigData
		});
		if(data.cb) {
			data.cb();
		}
	} else {
		// Something's weird
		ar.log({
			clientDeviceId: ar.state.clientDeviceId,
			code: 'GET-TVCONFIG-ERROR-2',
			message: 'An internal error occurred while trying to get TV Config data.',
			textStatus: textStatus,
			data: data,
			jqXHR_status: jqXHR.status,
			jqXHR_responseJSON: jqXHR.responseJSON
		});
		ar.setState({
			haveTvConfigData: false,
			tvConfigData: {}
		});
		const msg = ar.i18nJ.t('internalErrorOccurred');
		ar.showMessage(msg);
	}
};

ar.getTvConfigDataError = function getTvConfigDataError(jqXHR, textStatus, errorThrown) {
	ar.log({
		clientDeviceId: ar.state.clientDeviceId,
		code: 'GET-TVCONFIG-ERROR-1',
		message: 'An internal error occurred while trying to get TV Config data.',
		textStatus: textStatus,
		errorThrown: errorThrown,
		jqXHR_responseJSON: jqXHR.responseJSON
	});
	ar.setState({
		haveTvConfigData: false,
		tvConfigData: {}
	});
	//const msg = ar.i18nJ.t('internalErrorOccurred');
	// console.log("TV CONFIG ERROR HERE")
	//ar.showMessage(msg);
};

ar.getTvConfigData = function getTvConfigData(cb) {
	if ( ! this.state.arToken ) { return; }

	ar.request({
		url         : '/api/v1/tvconfig',
		method      : 'GET',
		timeout     : 10000,
		headers     : {
			'Authorization': 'Bearer ' + ar.state.arToken
		},
		data        : null,
		fSuccess    : function(data, textStatus, jqXHR){
			data.cb = cb;
			ar.getTvConfigDataSuccess(data, textStatus, jqXHR);
		},
		fError      : ar.getTvConfigDataError,
		contentType : 'application/json; charset=UTF-8',
		dataType    : 'json'
	});
};

ar.saveTvConfigDataSuccess = function saveTvConfigDataSuccess(data, textStatus, jqXHR) {
	if ( jqXHR.status === 200 ) {	
		// Quiet; UI will show active/inactive TV buttons as appropriate
	} else {
		// Something's weird
		ar.log({
			clientDeviceId: ar.state.clientDeviceId,
			code: 'SAVE-TVCONFIG-ERROR-2',
			message: 'An internal error occurred while trying to save TV Config data.',
			textStatus: textStatus,
			data: data,
			jqXHR_status: jqXHR.status,
			jqXHR_responseJSON: jqXHR.responseJSON
		});
		const msg = ar.i18nJ.t('internalErrorOccurred');
		ar.showMessage(msg);
	}
};

ar.saveTvConfigDataError = function saveTvConfigDataError(jqXHR, textStatus, errorThrown) {
	ar.log({
		clientDeviceId: ar.state.clientDeviceId,
		code: 'SAVE-TVCONFIG-ERROR-1',
		message: 'An internal error occurred while trying to save TV Config data.',
		textStatus: textStatus,
		errorThrown: errorThrown,
		jqXHR_responseJSON: jqXHR.responseJSON
	});
	const msg = ar.i18nJ.t('internalErrorOccurred');
	ar.showMessage(msg);
};

ar.saveTvConfigData = function saveTvConfigData() {
	ar.request({
		url         : '/api/v1/tvconfig',
		method      : 'POST',
		timeout     : 10000,
		headers     : {
			'Authorization' : `Bearer ${ar.state.arToken}`
		},
		data        : JSON.stringify({
			tvConfigData : ar.state.tvConfigData
		}),
		fSuccess    : ar.saveTvConfigDataSuccess,
		fError      : ar.saveTvConfigDataError,
		contentType : 'application/json; charset=UTF-8',
		dataType    : 'json'
	});
};

ar.getDevicesSuccess = function getDevicesSuccess(data, textStatus, jqXHR) {
	if ( jqXHR.status === 200 && data && data.devices && data.devices.length > 0 ) {
		// We got a 200 with the data we want/need
		if (data.devices.length === 1) {
			ar.setState({
				devices             : data.devices,
				selectedXboDeviceId : data.devices[0].id,
				selectedXboDeviceName : data.devices[0].name
			});
			ar.saveLocal('devices', JSON.stringify(data.devices));
			ar.saveLocal('deviceId', data.devices[0].id);
			ar.saveLocal('deviceName', data.devices[0].name);
			ar.getARToken(); // Will go to /remote if successful, /error if not
		} else {
			ar.setState({
				devices : data.devices
			});
			ar.saveLocal('devices', JSON.stringify(data.devices));
			if ( window.location.pathname !== '/choose-device' ) {
				arRouter.push('/choose-device');
			}
		}
	} else {
		// Something's weird
		ar.log({
			clientDeviceId: ar.state.clientDeviceId,
			code: 'CHOOSE-DEVICE-ERROR-2',
			message: 'No compatible set top box found.',
			textStatus: textStatus,
			data: data,
			jqXHR_status: jqXHR.status,
			jqXHR_responseJSON: jqXHR.responseJSON
		});
		const msg = ar.i18nJ.t('noCompatibleStbFound');
		arRouter.push('/error?msg='+msg);
	}
};

ar.getDevicesError = function getDevicesError(jqXHR, textStatus, errorThrown) {
	// Could be a 415, 405, 400, 423, 401, ... or 500
	ar.log({
		clientDeviceId: ar.state.clientDeviceId,
		code: 'CHOOSE-DEVICE-ERROR-1',
		message: 'An internal error occurred while trying to get a list of devices for your account.',
		textStatus: textStatus,
		errorThrown: errorThrown,
		jqXHR_responseJSON: jqXHR.responseJSON
	});
	const msg = ar.i18nJ.t('internalErrorGettingDevs');
	arRouter.push('/error?msg='+msg);
};

ar.getDevices = function getDevices() {
	ar.request({
		url         : '/api/v1/devices',
		method      : 'POST',
		timeout     : 10000,
		headers     : {
			'Authorization': 'Bearer ' + ar.state.arToken
		},
		data        : null,
		fSuccess    : ar.getDevicesSuccess,
		fError      : ar.getDevicesError,
		contentType : 'application/json; charset=UTF-8',
		dataType    : 'json'
	});
};

ar.getARTokenSuccess = function getARTokenSuccess(data, textStatus, jqXHR) {
	if ( jqXHR.status === 200 && data && data.arToken ) {
		// We got a 200 with the data we want/need
		ar.login(data);
		if ( window.location.pathname !== '/remote' ||  window.location.pathname !== '/my-remote') {
			ar.goToCorrectRemotePage();
		}
	} else {
		// Something's weird
		ar.log({
			clientDeviceId: ar.state.clientDeviceId,
			code: 'GET-ARTOKEN-ERROR-2',
			message: 'An unusual internal error occurred trying to get token.',
			textStatus: textStatus,
			data: data,
			jqXHR_status: jqXHR.status,
			jqXHR_responseJSON: jqXHR.responseJSON
		});
		const msg = ar.i18nJ.t('internalErrorGettingToken');
		arRouter.push('/error?msg='+msg);
	}
};

ar.getARTokenError = function getARTokenError(jqXHR, textStatus, errorThrown) {
	if (jqXHR.status === 401) {
		// AR Token has expired. Either Easy Pair or ID token expired.
		ar.removeLocal('arToken');
		ar.removeLocal('idToken');
		ar.removeLocal('partner');
		ar.setState({
			confirmInterval : null,
			arToken         : null
		});
		$('body').css('background-color', '#2a2c2d'); /* HACK! */
		arRouter.push('/');
		const msg = ar.i18nJ.t('yourSessionExpired');
		ar.showMessage(msg);
		return;
	}
	// Could be a 415, 405, 400, 423, 401, ... or 500
	ar.log({
		clientDeviceId: ar.state.clientDeviceId,
		code: 'GET-ARTOKEN-ERROR-1',
		message: 'An internal error occurred while authorizing your account.',
		textStatus: textStatus,
		errorThrown: errorThrown,
		jqXHR_responseJSON: jqXHR.responseJSON
	});
	const msg = ar.i18nJ.t('internalErrorGettingToken');
	arRouter.push('/error?msg='+msg);
};

ar.getARToken = function getARToken() {
	ar.request({
		url         : '/api/v1/auth/token',
		method      : 'POST',
		timeout     : 10000,
		headers     : {
			'Authorization': 'Bearer ' + ar.state.arToken
		},
		data        : JSON.stringify({
			selectedXboDeviceId : ar.state.selectedXboDeviceId
		}),
		fSuccess    : ar.getARTokenSuccess,
		fError      : ar.getARTokenError,
		contentType : 'application/json; charset=UTF-8',
		dataType    : 'json'
	});
};

ar.fetchTokenFromAuthCode = function fetchTokenFromAuthCode(authCode) {
	// The component loading works this way that
	// ar.state.clientDeviceId is not set when this function is called
	const clientDeviceId = ar.getLocal('clientDeviceId');
	ar.request({
		url: '/api/v1/auth/token/code',
		method: 'POST',
		timeout: 10000,
		headers: {},
		data: JSON.stringify({
			partner: ar.state.partner,
			authCode: authCode,
			clientDeviceId: clientDeviceId
		}),
		fSuccess: ar.fetchTokenFromAuthCodeSuccess,
		fError: ar.fetchTokenFromAuthCodeError,
		contentType: 'application/json; charset=UTF-8',
		dataType: 'json'
	});
}

ar.fetchTokenFromAuthCodeSuccess = function fetchTokenFromAuthCodeSuccess(data, textStatus, jqXHR) {
	if ( jqXHR.status === 200 && data && data.arToken ) {
		// We got a 200 with the data we want/need
		ar.login(data);
		ar.getDevices();
	} else {
		// Something's weird
		ar.log({
			clientDeviceId: ar.state.clientDeviceId,
			code: 'GET-FETCHTOKEN-ERROR-2',
			message: 'An unusual internal error occurred trying to get token.',
			textStatus: textStatus,
			data: data,
			jqXHR_status: jqXHR.status,
			jqXHR_responseJSON: jqXHR.responseJSON
		});
		const msg = ar.i18nJ.t('internalErrorGettingToken');
		arRouter.push('/error?msg='+msg);
	}
}

ar.fetchTokenFromAuthCodeError = function fetchTokenFromAuthCodeError(jqXHR, textStatus, errorThrown) {
	// Could be a 415, 405, 400, 423, 401, ... or 500
	ar.log({
		clientDeviceId: ar.state.clientDeviceId,
		code: 'GET-FETCHTOKEN-ERROR-1',
		message: 'An internal error occurred while authorizing your account.',
		textStatus: textStatus,
		errorThrown: errorThrown,
		jqXHR_responseJSON: jqXHR.responseJSON
	});
	const msg = ar.i18nJ.t('internalErrorGettingToken');
	arRouter.push('/error?msg='+msg);
};

ar.login = function login(data) {
	ar.setState({
		arToken: data.arToken,
		refreshIntervalMs: data.refreshIntervalMs,
		isLoggedIn: true
	});
	ar.saveLocal('arToken', data.arToken);
	ar.saveLocal('refreshIntervalMs', data.refreshIntervalMs);

	// Set up refresh token polling, if needed
	ar.setupRefreshTokenPolling();
};

ar.saveCompanionPairingSuccess = function saveCompanionPairingSuccess(data, textStatus, jqXHR) {
	//Do Nothing
};

ar.saveCompanionPairingError = function saveCompanionPairingError(jqXHR, textStatus, errorThrown) {
	// Could be a 415, 405, 400, 423, 401, ... or 500
	ar.log({
		clientDeviceId: ar.state.clientDeviceId,
		code: 'SAVE-COMPANION-PAIRING-ERROR-1',
		message: 'An internal error occurred while trying to save companion pairing data.',
		textStatus: textStatus,
		errorThrown: errorThrown,
		jqXHR_responseJSON: jqXHR.responseJSON
	});
	const msg = ar.i18nJ.t('internalErrorSavingCompanionPairing'); // @TODO: Replace with a different/real message key + messages
	arRouter.push('/error?msg='+msg);
};

ar.saveCompanionPairing = function saveCompanionPairing(companionId) {
	ar.request({
		url         : '/api/v1/companion/pairing',
		method      : 'POST',
		timeout     : 10000,
		headers     : {
			'Authorization' : `Bearer ${ar.state.arToken}`
		},
		data        : JSON.stringify({
			partner        : ar.state.partner,        // For debugging/logging
			clientDeviceId : ar.state.clientDeviceId, // For debugging/logging
			companionId    : companionId
		}),
		fSuccess    : ar.saveCompanionPairingSuccess,
		fError      : ar.saveCompanionPairingError,
		contentType : 'application/json; charset=UTF-8',
		dataType    : 'json'
	});
};

ar.refreshTokenError = function refreshTokenError(jqXHR, textStatus, errorThrown) {
	// cleanup refresh token polling
	ar.clearRefreshTokenPolling();
	if (jqXHR.status === 500) {
		// if there is a 500, potentially we can retry refreshing a token
		ar.log({
			clientDeviceId: ar.state.clientDeviceId,
			code: 'REFRESH-TOKEN-FAILURE',
			message: 'Attempt to refresh token failed'
		});
		ar.clearRefreshTokenPolling();
		// Show user that the it's our fault
		const msg = ar.i18nJ.t('wereSorryInternalError');
		ar.showMessage(msg);
	} else {
		// there is not much we can do, user has to log in again
		ar.removeLocal('arToken');
		$('body').css('background-color', 'black'); /* HACK! */
		ar.setState({
			confirmInterval : null,
			arToken         : null
		});
		const msg = ar.i18nJ.t('sessionIsNoLongerValid');
		arRouter.push('/login-again?msg='+msg);
	}
}

ar.refreshTokenSuccess = function refreshTokenSuccess(data, textStatus, jqXHR) {
	if ( jqXHR.status === 200 && data && data.arToken ) {
		// We got a 200 with the data we want/need
		ar.login(data);
		if(data.cb) {
			data.cb(null, null);
		}
	} else {
		// Something's weird
		ar.log({
			clientDeviceId: ar.state.clientDeviceId,
			code: 'REFRESH-TOKEN-ERROR-2',
			message: 'An unusual internal error occurred trying to refresh token.',
			textStatus: textStatus,
			data: data,
			jqXHR_status: jqXHR.status,
			jqXHR_responseJSON: jqXHR.responseJSON
		});
		// If we were going to reveal that an underlying error occurred:
		//const msg = ar.i18nJ.t('wereSorryInternalError');
		//arRouter.push('/error?msg='+msg);
		// Treat an inability to refresh the token with a gentle "you need to re-login" UX flow
		const msg = ar.i18nJ.t('sessionIsNoLongerValid');
		arRouter.push('/login-again?msg='+msg);
	}
}

ar.refreshToken = function refreshToken(cb) {
	ar.request({
		url         : '/api/v1/auth/token/refresh',
		method      : 'POST',
		timeout     : 10000,
		headers     : {
			'Authorization': 'Bearer ' + ar.state.arToken
		},
		data        : null,
		fSuccess    : function(data, textStatus, jqXHR){
			data.cb = cb;
			ar.refreshTokenSuccess(data, textStatus, jqXHR);
		},
		fError      : ar.refreshTokenError,
		contentType : 'application/json; charset=UTF-8',
		dataType    : 'json'
	});
}

ar.setupRefreshTokenPolling = function setupRefreshTokenPolling() {
	ar.clearRefreshTokenPolling();
	const refreshIntervalMs = parseInt(ar.getLocal('refreshIntervalMs', 10)) || ar.state.refreshIntervalMs;
	// in case of easy pair, no need to set up refreshing, the value of this var will be null (default)
	if (!refreshIntervalMs) return;
	const refreshIntervalTimer = setInterval(ar.refreshToken, refreshIntervalMs);
	ar.setState({
		refreshIntervalTimer
	})
	ar.log({
		clientDeviceId: ar.state.clientDeviceId,
		code: 'REFRESH-TOKEN-POLLING',
		message: 'Successfully initialized refresh token polling',
		refreshIntervalMs: refreshIntervalMs
	});
}

ar.clearRefreshTokenPolling = function clearRefreshTokenPolling() {
	if (ar.state.refreshIntervalTimer) {
		clearInterval(ar.state.refreshIntervalTimer);
		ar.setState({ refreshIntervalTimer: null });
	}
}

// Called after redirect from Xerxes returns to /logout-complete (see  LogoutCompletePage)
// Keep clientDeviceId identity and partner
ar.logout = function logout() {
	ar.removeLocal('arToken');
	ar.removeLocal('deviceId');
	ar.removeLocal('deviceName');
	ar.removeLocal('devices');
	ar.removeLocal('partner');
	ar.removeLocal('refreshIntervalMs');
	ar.setState({
		arToken               : null,
		selectedXboDeviceId   : null,
		selectedXboDeviceName : null,
		isLoggedIn            : false,
		devices               : null,
		refreshIntervalMs     : null
	});
	arRouter.push('/');
	ar.clearRefreshTokenPolling();
};

ar.goToCorrectRemotePage = function goToCorrectRemotePage() {
	const customMode = ar.getLocalBoolean('customMode', false);
	if ( customMode ) {
		arRouter.push('/my-remote');
	} else {
		arRouter.push('/remote');
	}
};

// Normally called with (undefined, undefined) or (null, null)
// But if user gives query string parameters (cd and ept), they'll be used.
ar.init = function init(qsClientDeviceId, qsARToken) {
	// Retrieve or create/store a clientDeviceId
	ar.ensureClientDeviceId(qsClientDeviceId);

	// Bring back state from local storage, if present
	const devices = JSON.parse(ar.getLocal('devices'));
	if ( devices ) {
		ar.setState({ devices: devices });
	}
	const deviceId = ar.getLocal('deviceId');
	const deviceName = ar.getLocal('deviceName');
	if ( deviceId ) {
		ar.setState({ selectedXboDeviceId: deviceId });
	}
	if ( deviceName ) {
		ar.setState({ selectedXboDeviceName: deviceName });
	}

	const isCompanionMode = ar.getLocalBoolean('companionMode', false);

	// Clear "My Remote" stuff
	ar.setState({
		isFetchingSeaButtons: false,
		haveSeaButtonsData: false,
		seaButtonsData: []
	});

	// See if the user is already logged in
	let arToken = ar.getLocal('arToken');

	if ( arToken ) {
		// Save into state
		ar.setState({
			arToken: arToken
		});

		// User is already logged in
		if ( ! isCompanionMode ) {
			// Refresh right away because we don't know for how long the app has been "offline"/closed
			ar.refreshToken(function(err, data) {
				if ( err ) { return; }

				// Get user's buttons
				ar.getSeaButtonsData();

				// Get popular buttons
				ar.getPopularSeaButtons();
			});

			// Set up refresh token polling, if needed
			ar.setupRefreshTokenPolling();

			// Go to the "remote" page or the "my-remote" page and "start the app"
			ar.setState({ arToken: arToken });
			if ( ! ['/remote', '/my-remote'].includes(window.location.pathname) ) {
				ar.goToCorrectRemotePage();
			}
		}

	} else if ( qsARToken ) {
		// We were given an ARToken in the query string... use it
		ar.saveLocal('arToken', qsARToken);
		ar.setState({ arToken: qsARToken });

		// Refresh right away because we don't know for how long the app has been "offline"/closed
		ar.refreshToken(function() {
			if ( err ) { return; }

			// Get user's buttons
			ar.getSeaButtonsData();

			// Get popular buttons
			ar.getPopularSeaButtons();
		});

		// Set up refresh token polling, if needed
		ar.setupRefreshTokenPolling();

		if ( ! ['/remote', '/my-remote'].includes(window.location.pathname) ) {
			ar.goToCorrectRemotePage();
		}

	} else {
		// If we get here, the user needs to pair or login; don't let her go 'directly' to a place she's not supposed to go

		if ( isCompanionMode ) {
			if ( window.location.pathname !== '/companion' ) {
				arRouter.replace('/companion');
			}
		} else {
			if ( window.location.pathname !== '/' &&
				 window.location.pathname !== '/login-complete' &&
				 window.location.pathname !== '/choose-device' &&
				 window.location.pathname !== '/error' ) {
				arRouter.replace('/');
			}
		}
	}
};

ar.processKeySuccess = function processKeySuccess(data, textStatus, jqXHR) {
	if ( jqXHR.status === 200 ) {
		// Do nothing
	} else {
		// Something's weird...
		ar.log({
			clientDeviceId: ar.state.clientDeviceId,
			code: 'PROCESS-KEY-UNEXPECTED-STATUS-CODE',
			message: 'An unexpected status code occurred trying to process a key',
			textStatus: textStatus,
			data: data,
			jqXHR_status: jqXHR.status,
			jqXHR_responseJSON: jqXHR.responseJSON
		});
		// Do nothing (don't call user's attention to this)
	}
};

ar.processKeyError = function processKeyError(jqXHR, textStatus, errorThrown) {
	if (jqXHR.status === 401) {
		// AR Token has expired. Either Easy Pair or ID token expired.
		ar.removeLocal('arToken');
		ar.removeLocal('idToken');
		ar.removeLocal('partner');
		ar.setState({
			confirmInterval : null,
			arToken         : null
		});
		$('body').css('background-color', 'black'); /* HACK! */
		const msg = ar.i18nJ.t('sessionIsNoLongerValid');
		arRouter.push('/login-again?msg='+msg);
		return;
	}

	// @todo verify if these conditions are needed
	if ( jqXHR.responseJSON && jqXHR.responseJSON.status === 'EXPIRED-EASYPAIR-TOKEN' ) {
		// Expired EasyPair token
		// Undo the EasyPair pairing (but keep clientDeviceId identity)
		ar.removeLocal('arToken');
		arRouter.push('/?msg=EXPIRED-EASYPAIR-TOKEN');
		return;
	} else if ( jqXHR.responseJSON && jqXHR.responseJSON.status === 'INVALID-KEY' ) {
		ar.log({
			clientDeviceId: ar.state.clientDeviceId,
			code: 'PROCESS-KEY-INVALID-KEY',
			message: 'User pressed a key that could not be processed'
		});
		// Show user that the key didn't work
		const msg = ar.i18nJ.t('couldNotProcessKey');
		ar.showMessage(msg);
	} else if ( jqXHR.responseJSON && jqXHR.responseJSON.status === 'EXPIRED-XAPI-TOKEN' ) {
		// The psid has expired. Nothing can really be done.
		arRouter.push('/?msg=EXPIRED-XAPI-TOKEN');
		return;
	} else {
		// Some other kind of XAPI error
		ar.log({
			clientDeviceId: ar.state.clientDeviceId,
			code: 'PROCESS-KEY-INTERNAL-ERROR-1',
			message: 'An internal error occurred trying to process a key',
			textStatus: textStatus,
			errorThrown: errorThrown,
			jqXHR_responseJSON: jqXHR.responseJSON
		});
	}
};

// Send processKey request
ar.processKey = function processKey(vcode) {
	ar.request({
		url         : '/api/v1/processKey',
		method      : 'POST',
		timeout     : 2000,
		headers     : {
			'Authorization': 'Bearer ' + ar.state.arToken
		},
		data        : JSON.stringify({
			vcode          : vcode
		}),
		fSuccess    : ar.processKeySuccess,
		fError      : ar.processKeyError,
		contentType : 'application/json; charset=UTF-8',
		dataType    : 'json'
	});
};

ar.processASLSuccess = function processASLSuccess(data, textStatus, jqXHR) { 
	// console.log(Object.keys(data));
	if ( jqXHR.status === 200 ) { 
		ar.setState({aslCommand: data});//how to send this back to ASLInterface.vue
		// console.log("Result in success: " + JSON.stringify(data))
		if(data.cb) {
			data.cb(null, data);
		}
	} else {
		// Something's weird...
		if(data.cb){
			data.cb({status: 'network error'}, null);
			//ar.showMessage(msg); //allows us to do pink bar, may not be something here 
		}
		ar.log({
			
		});
		// Do nothing (don't call user's attention to this)
	}
};

ar.processASLError = function processASLError(jqXHR, textStatus, errorThrown, cb) {
	if(cb) {
		cb(errorThrown, null);
	}
};

// Send Sign-Speak request
ar.processASL = function processASL(fullBase64data, cb) {
	ar.request({
		url         : '/api/v1/asl',
		method      : 'POST',
		timeout     : 22000, //e2e timeout, test timing
		headers     : {
			'Authorization': 'Bearer ' + ar.state.arToken,
		},
		data :  JSON.stringify({base64data: fullBase64data.split(',')[1]}),
		fSuccess    : function(data, textStatus, jqXHR){
			data.cb = cb;
			ar.processASLSuccess(data, textStatus, jqXHR);
		},
		fError      : function(jqXHR, textStatus, errorThrown) {
			ar.processASLError(jqXHR, textStatus, errorThrown, cb)
		  },
		contentType : 'application/json; charset=UTF-8',
		dataType    : 'json'
	});
};

ar.processASLFeedbackSuccess = function processASLFeedbackSuccess(feedbackData, textStatus, jqXHR) {
	// successfully sent data to sign-speak feedback api
	if ( jqXHR.status === 200 ) { 
		if(feedbackData.cb) {
			feedbackData.cb(null, feedbackData);
		}
	} else {
		// Something's weird...
		if(feedbackData.cb){
			feedbackData.cb({status: 'network error'}, null);
		}
	}
}

ar.processASLFeedbackError = function processASLFeedbackError(jqXHR, textStatus, errorThrown, cb) {
	// error sending data to sign-speak feedback api
	if(cb) {
		cb(errorThrown, null);
	}
}

ar.processASLFeedback = function processASLFeedback(feedbackData, cb) {
	// Send data to ASL vendor feedback api
		ar.request({
		url         : '/api/v1/aslFeedback',
		method      : 'POST',
		timeout     : 20000,
		headers     : {
			'Authorization': 'Bearer ' + ar.state.arToken,
		},
		data :  JSON.stringify({data: feedbackData}),
		fSuccess	: function(data, textStatus, jqXHR){
			data.cb = cb;
			ar.processASLFeedbackSuccess(data, textStatus, jqXHR);
		},
		fError      : function(jqXHR, textStatus, errorThrown) {
			ar.processASLFeedbackError(jqXHR, textStatus, errorThrown, cb)
		  },
		contentType : 'application/json; charset=UTF-8',
		dataType    : 'json'
	});
}

ar.processCecKeySuccess = function processCecKeySuccess(data, textStatus, jqXHR) {
	if ( jqXHR.status === 200 ) {
		// Do nothing
	} else {
		// Something's weird...
		ar.log({
			clientDeviceId: ar.state.clientDeviceId,
			code: 'PROCESS-CECC-KEY-UNEXPECTED-STATUS-CODE',
			message: 'An unexpected status code occurred trying to process a CEC key',
			textStatus: textStatus,
			data: data,
			jqXHR_status: jqXHR.status,
			jqXHR_responseJSON: jqXHR.responseJSON
		});
		// Do nothing (don't call user's attention to this)
	}
};

ar.processCecKeyError = function processCecKeyError(jqXHR, textStatus, errorThrown) {
	if (jqXHR.status === 401) {
		// AR Token has expired. Either Easy Pair or ID token expired.
		ar.removeLocal('arToken');
		ar.removeLocal('idToken');
		ar.removeLocal('partner');
		ar.setState({
			confirmInterval : null,
			arToken         : null
		});
		$('body').css('background-color', 'black'); /* HACK! */
		const msg = ar.i18nJ.t('sessionIsNoLongerValid');
		arRouter.push('/login-again?msg='+msg);
		return;
	}

	// @todo verify if these conditions are needed
	if ( jqXHR.responseJSON && jqXHR.responseJSON.status === 'EXPIRED-EASYPAIR-TOKEN' ) {
		// Expired EasyPair token
		// Undo the EasyPair pairing (but keep clientDeviceId identity)
		ar.removeLocal('arToken');
		arRouter.push('/?msg=EXPIRED-EASYPAIR-TOKEN');
		return;
	} else if ( jqXHR.responseJSON && jqXHR.responseJSON.status === 'INVALID-KEY' ) {
		ar.log({
			clientDeviceId: ar.state.clientDeviceId,
			code: 'PROCESS-KEY-INVALID-KEY',
			message: 'User pressed a key that could not be processed'
		});
		// Show user that the key didn't work
		const msg = ar.i18nJ.t('couldNotProcessKey');
		ar.showMessage(msg);
	} else if ( jqXHR.responseJSON && jqXHR.responseJSON.status === 'EXPIRED-XAPI-TOKEN' ) {
		// The psid has expired. Nothing can really be done.
		arRouter.push('/?msg=EXPIRED-XAPI-TOKEN');
		return;
	} else {
		// Some other kind of XAPI error
		ar.log({
			clientDeviceId: ar.state.clientDeviceId,
			code: 'PROCESS-CEC-KEY-INTERNAL-ERROR-1',
			message: 'An internal error occurred trying to process a CEC key',
			textStatus: textStatus,
			errorThrown: errorThrown,
			jqXHR_responseJSON: jqXHR.responseJSON
		});
	}
};

// Send processCecKey request
ar.processCecKey = function processCecKey(key, value) {
	ar.request({
		url         : '/api/v1/processCecKey',
		method      : 'POST',
		timeout     : 3000,
		headers     : {
			'Authorization': 'Bearer ' + ar.state.arToken
		},
		data        : JSON.stringify({
			key            : key,
			value          : value
		}),
		fSuccess    : ar.processCecKeySuccess,
		fError      : ar.processCecKeyError,
		contentType : 'application/json; charset=UTF-8',
		dataType    : 'json'
	});
};

// Send user-configured IoT button request (e.g., to IFTTT)
ar.processIotCommand = function processIotCommand(buttonKey, tvConfigData) {
	if ( !tvConfigData ) { return; }
	const serverUrl = `/api/v1/iot`;

	ar.request({
		url: serverUrl,
		method: 'POST',
		timeout: 5000,
		headers: {
			'Authorization': 'Bearer ' + ar.state.arToken
		},
		data: JSON.stringify({
			eventName: tvConfigData[buttonKey].eventName,
			apiKey: tvConfigData.apiKey,
		}),
		fSuccess: function(data, textStatus, jqXHR) {
			if ( ar.state.keySound ) { ar.sound('_USE_DEFAULT_SOUND_') };
		},
		fError: function(jqXHR, textStatus, errorThrown) {
			if ( ar.state.keySound ) { ar.sound('ENTER') };
		},
		contentType: 'application/json; charset=UTF-8',
		dataType : 'json'
	});
};

// NOTE: buttonKey is used for IoT-related user-configured TV buttons functionality
//       key and value are used for CEC-related functionality
ar.processTvKey = function processTvKey(buttonKey, key, value) {
	// If user has configured TV buttons using the IoT functionality,
	// make the button do what she wants it to do
	if ( ar.state.tvConfigData[buttonKey] &&
			ar.state.tvConfigData[buttonKey].eventName ) {
		ar.processIotCommand(buttonKey, ar.state.tvConfigData);
	} else {
		if ( ar.state.keySound ) { ar.sound(vcode); }
		ar.processCecKey(key, value);
	}
};

ar.submitTextCommand = 	function submitTextCommand(textCommand) {
	ar.request({
		url: '/api/v1/text',
		method: 'POST',
		timeout: 7000,
		headers: {},
		data: JSON.stringify({
			cmd     : textCommand,
			arToken : ar.state.arToken
		}),
		fSuccess: function(data, textStatus, jqXHR) {
			// console.log('Text command worked: ' + textCommand);
			// Quiet success; the TV will show reaction/results
		},
		fError: function(jqXHR, textStatus, errorThrown) {
			// console.log('Text command did not work');
			// Quiet failure; the TV will show or not show results
		},
		contentType: 'application/json; charset=UTF-8',
	});
};

ar.processSeaButtonSuccess = function processSeaButtonSuccess(data, textStatus, jqXHR) {
	if ( jqXHR.status === 200 ) {
		// Do nothing
	} else {
		// Something's weird...
		ar.log({
			clientDeviceId: ar.state.clientDeviceId,
			code: 'PROCESS-SEABUTTON-UNEXPECTED-STATUS-CODE',
			message: 'An unexpected status code occurred trying to process a SEA Button',
			textStatus: textStatus,
			data: data,
			jqXHR_status: jqXHR.status,
			jqXHR_responseJSON: jqXHR.responseJSON
		});
		// Do nothing (don't call user's attention to this)
	}
};

ar.tvButtons = function () {
	return [
		{
			key: 'mute',
			label: 'Mute',
			id: 'MUTE',
			cecKey: 'MUTE',
			cecValue: 'ON'
		},
		{
			key: 'power',
			label: 'Power',
			id: 'ALLPOWER',
			cecKey: 'POWER',
			cecValue: 'OFF'
		},
		{
			key: 'tvInput',
			label: 'TV Input',
			id: 'TVINPUT',
			cecKey: 'INPUT',
			cecValue: 'STB_INPUT'
		},
		{
			key: 'tvPower',
			label: 'TV Power',
			id: 'TVPOWER',
			cecKey: 'POWER',
			cecValue: 'OFF'
		},
		{
			key: 'volumeDown',
			label: 'Volume Down',
			id: 'VOLUME_DOWN',
			cecKey: 'VOLUME',
			cecValue: 'DOWN',
		},
		{
			key: 'volumeUp',
			label: 'Volume Up',
			id: 'VOLUME_UP',
			cecKey: 'VOLUME',
			cecValue: 'UP'
		},
	];
};

// Enable appropriate buttons based on user's TV Configuration using IFTTT, etc.
ar.enableTvButtons = function() {
	const buttons = ar.tvButtons();
	buttons.forEach(function(button) {
		if ( ar.state.tvConfigData[button.key] ) {
			if ( ar.state.tvConfigData[button.key].eventName ) {
				$(`#${button.id}`).removeAttr('disabled');
			}
		}
	});
};

// Enable appropriate buttons based on which CEC keys are programatically controllable
ar.enableCecButtons = function() {
	var cecButtons = {
		POWER: ['#TVPOWER', '#ALLPOWER'],
		VOLUME: ['#VOLUME_UP', '#VOLUME_DOWN'],
		MUTE: ['#MUTE'],
		INPUT: ['#TV_INPUT']
	};

	// Enable buttons that are not currently supported by checkCapabilities call
	//$('#VOLUME_UP').removeAttr('disabled');
	//$('#VOLUME_DOWN').removeAttr('disabled');

	ar.request({
		url: '/api/v1/checkCapabilities',
		method: 'POST',
		timeout: 3000,
		headers: {
			'Authorization': 'Bearer ' + ar.state.arToken
		},
		processData: false,
		data: null,
		fSuccess: function (data, textStatus, jqXHR) {
			// data.results contains:
			// - hdmiLinkReady,
			// - tvPowerOnStatusAvailable, tvPowerOffStatusAvailable,
			// - tvPowerOnCommandAvailable, tvPowerOffCommandAvailable
			// each of which can be "yes", "no", or "unknown"
			// Handy for debugging: Append "true||" to each of the next two lines to enable buttons always
			if (data.results.hdmiLinkReady === "yes") {
				if (data.results.tvPowerOnCommandAvailable === "yes" || data.results.tvPowerOffCommandAvailable === "yes") {
					cecButtons.POWER.forEach(function (btn) {
						$(btn).removeAttr('disabled');
					});
				}
			}
		},
		fError: function (jqXHR, textStatus, errorThrown) {
			// Quiet failure; the buttons won't be enabled
			ar.log({
				clientDeviceId: ar.state.clientDeviceId,
				code: 'CHECK-CAPABILITY-ERROR',
				message: 'An unusual internal error occurred checking CEC capabilities',
				jqXHR_status: jqXHR.status,
				jqXHR_responseJSON: jqXHR.responseJSON,
				textStatus: textStatus,
				errorThrown: errorThrown
			});
		},
		contentType: 'application/json; charset=UTF-8',
	});
}

window.ar = ar;
export default ar;
